import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccordionDetails from '@mui/material/AccordionDetails/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary/AccordionSummary';
import Accordion from 'common/components/material/Accordion';
import Typography from 'common/components/material/Typography';
import ListAltIcon from '@mui/icons-material/ListAlt';
import useTheme from '@mui/material/styles/useTheme';
import Stack from 'common/components/material/Stack';
import { SkillGroups } from './MatchingEngineComponents/ProfileSkillBreakdown';
import { matchingEngineApi } from '../matching-engine.utils';
import useSession from 'common/hooks/useSession';
import useQuery from 'common/hooks/useQuery';
import CircularProgress from 'common/components/material/CircularProgress';
import Box from 'common/components/material/Box';
import moment from 'moment';

type SkillMetadataType =
  | 'ASSET_CLASS'
  | 'COMPONENT'
  | 'COMPONENT_TECHNOLOGY'
  | 'MARKET'
  | 'PROTOCOL'
  | 'SUB_COMPONENT'
  | 'VENUE_TYPE'
  | 'SKILL'
  | 'PRODUCT'
  | 'WORKFLOW_TYPE';

type SkillHighlight = {
  evidenceCount?: number;
  /** Compatibility with highlight responses cached before evidence-backed summaries. */
  activitiesCount?: number;
  positiveEvidenceCount?: number;
  negativeEvidenceCount?: number;
  recentHighCriticalityCount?: number;
  latestEvidenceDate?: string;
  skills: { id: string; name: string }[];
  related?: string[];
  roles?: Array<{ name: string; count: number }>;
  sources?: Array<{ name: string; count: number }>;
  sourceSystems?: string[];
  evidenceTypes?: Array<{
    name: string;
    count: number;
    subtypes: Array<{ name: string; count: number }>;
  }>;
};

export type Highlights = {
  [key in SkillMetadataType]?: SkillHighlight;
} & {
  PERFORMANCE_RANK?: number;
};

export type StructuredHighlightsTextPart =
  | string
  | {
      text: string;
      bold?: boolean;
      italic?: boolean;
    };

export type StructuredHighlights = Record<
  string,
  {
    type: 'positive' | 'negative';
    parts: StructuredHighlightsTextPart[];
  }
>;

export const useMatchingProfileHighlights = ({
  person,
  skillIds,
}: {
  person: any;
  skillIds: string[];
}) => {
  const { account: user } = useSession();
  const profileID = person.profileID ?? person.profileId;
  const getData = () => {
    const params = { skillIds };
    return matchingEngineApi.get(`profiles/${profileID}/highlights`, { params });
  };

  const queryKey = [
    'matching-engine',
    user?.organizationId,
    profileID,
    'evidence-highlights-v2',
    skillIds.join(','),
  ];

  const queryResponse = useQuery(queryKey, getData, {
    enabled: !!profileID && skillIds.length > 0,
  });

  return {
    ...queryResponse,
    data: queryResponse.data?.data as Highlights,
  };
};

export const MatchingEnginePersonDrawerSummary: React.FC<{
  person: any;
  skillGroups: SkillGroups;
}> = ({ person, skillGroups }) => {
  const { palette } = useTheme();
  const skillIds = new Set<string>();
  for (const skill of skillGroups.inferredSkills ?? []) {
    skillIds.add(skill.id);
  }
  for (const skill of skillGroups.managerVerifiedSkills ?? []) {
    skillIds.add(skill.id);
  }
  const { data: highlights, isLoading } = useMatchingProfileHighlights({
    person,
    skillIds: Array.from(skillIds),
  });
  if (skillIds.size === 0 && (skillGroups?.missingSkills?.length ?? 0) === 0) return null;
  return (
    <Box pt={2}>
      <Accordion
        defaultExpanded
        disableGutters
        sx={{
          background: palette.Extras.Extra13,
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" spacing={1}>
            <ListAltIcon />
            <Typography fontWeight="600" fontSize="0.95rem">
              Summary
            </Typography>
            {isLoading && <CircularProgress size="1.75rem" />}
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <ProfileHighlights
            structured={compileStructuredHighlights({ highlights, skillGroups })}
          />
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export const ProfileHighlights: React.FC<{
  structured: StructuredHighlights;
}> = ({ structured }) => {
  return (
    <ul style={{ paddingInlineStart: '0.75rem', marginBlockStart: 0, marginBlockEnd: 0 }}>
      {Object.keys(structured).map((skillMetaType) => {
        const { type, parts } = structured[skillMetaType as keyof StructuredHighlights]!;
        return (
          <li
            key={skillMetaType}
            style={{ listStyleType: type === 'positive' ? 'plus' : 'dash' }}
          >
            {renderParts(parts)}
          </li>
        );
      })}
    </ul>
  );

  function renderParts(parts: StructuredHighlightsTextPart[]) {
    return parts.map((part) => {
      if (typeof part === 'string') {
        return part;
      }
      if (part.bold) {
        return <strong>{part.text}</strong>;
      }
      if (part.italic) {
        return <i>{part.text}</i>;
      }
      return part.text;
    });
  }
};

export function compileStructuredHighlights({
  highlights,
  skillGroups,
}: {
  highlights?: Highlights;
  skillGroups: SkillGroups;
}) {
  const ret: StructuredHighlights = {};
  if (highlights) {
    for (const key of Object.keys(highlights)) {
      switch (key) {
        case 'ASSET_CLASS':
        case 'COMPONENT_TECHNOLOGY':
        case 'SKILL':
        case 'MARKET':
        case 'PROTOCOL':
        case 'SUB_COMPONENT':
        case 'VENUE_TYPE':
        case 'WORKFLOW_TYPE':
          ret[key] = {
            type: 'positive',
            parts: evidenceSummaryLabel(highlights[key]!),
          };
          appendEvidenceSignals(ret, key, highlights[key]!);
          break;

        case 'COMPONENT':
        case 'PRODUCT':
          ret[key] = { type: 'positive', parts: evidenceSummaryLabel(highlights[key]!) };
          appendEvidenceSignals(ret, key, highlights[key]!);
          break;

        case 'PERFORMANCE_RANK':
          const performanceRank = highlights[key]!;
          ret[key] = {
            type: [1, 2, 3].includes(performanceRank) ? 'positive' : 'negative',
            parts: performanceRankLabel(performanceRank),
          };
          break;
      }
    }
  }
  if (skillGroups.missingSkills && skillGroups.missingSkills.length > 0) {
    ret['MISSING_SKILLS'] = { type: 'negative', parts: missingSkillsLabel() };
  }
  return ret;

  function evidenceSummaryLabel(highlight: SkillHighlight) {
    const evidenceCount = getEvidenceCount(highlight);
    const { skills } = highlight;
    const parts: StructuredHighlightsTextPart[] = [];

    for (let i = 0; i < skills.length; i++) {
      if (i === 0) parts.push({ text: skills[i].name, bold: true });
      if (i > 0 && i < skills.length - 1)
        parts.push(`, `, { text: skills[i].name, bold: true });
      if (i > 0 && i === skills.length - 1)
        parts.push(` and `, { text: skills[i].name, bold: true });
    }

    parts.push(` experience is supported by `, { text: `${evidenceCount}`, bold: true });
    if (evidenceCount === 1) parts.push(` matching evidence item`);
    else parts.push(` matching evidence items`);
    appendEvidenceContext(parts, highlight);

    return parts;
  }

  function appendEvidenceContext(parts: StructuredHighlightsTextPart[], highlight: SkillHighlight) {
    const roles = compactBreakdown(highlight.roles);
    const sources = compactBreakdown(highlight.sources);

    if (roles.length) {
      parts.push('. Activity roles: ');
      appendList(
        parts,
        roles.map(({ name, count }) => ({ text: `${formatEvidenceLabel(name)} (${count})`, bold: false })),
      );
    }

    if (sources.length) {
      parts.push('. Evidence sources: ');
      appendList(
        parts,
        sources.map(({ name, count }) => ({ text: `${formatEvidenceLabel(name)} (${count})`, bold: false })),
      );
    }

    if (highlight.related?.length) {
      parts.push('. Related products include ');
      appendList(
        parts,
        highlight.related.map((name) => ({ text: name, italic: true })),
      );
    }

    parts.push('.');
  }

  function appendEvidenceSignals(result: StructuredHighlights, key: string, highlight: SkillHighlight) {
    const skillNames = highlight.skills.map(({ name }) => name).join(', ');
    const feedback = (highlight.evidenceTypes || []).find(({ name }) => name.toUpperCase() === 'SKILL_FEEDBACK');
    const positiveFeedbackCount = getSubtypeCount(feedback, 'UPVOTE');
    const negativeFeedbackCount = getSubtypeCount(feedback, 'DOWNVOTE');

    if (positiveFeedbackCount > 0) {
      result[`${key}_POSITIVE_FEEDBACK`] = {
        type: 'positive',
        parts: [
          `Received positive feedback `,
          { text: `${positiveFeedbackCount}`, bold: true },
          positiveFeedbackCount === 1 ? ` time for ` : ` times for `,
          { text: skillNames, bold: true },
          '.',
        ],
      };
    }

    const otherNegativeCount = Math.max(0, (highlight.negativeEvidenceCount || 0) - negativeFeedbackCount);
    if (negativeFeedbackCount > 0 || otherNegativeCount > 0) {
      const parts: StructuredHighlightsTextPart[] = [];
      if (negativeFeedbackCount > 0) {
        parts.push(
          `Received negative feedback `,
          { text: `${negativeFeedbackCount}`, bold: true },
          negativeFeedbackCount === 1 ? ` time` : ` times`,
        );
      }
      if (otherNegativeCount > 0) {
        if (parts.length) parts.push(' and has ');
        parts.push(
          { text: `${otherNegativeCount}`, bold: true },
          otherNegativeCount === 1 ? ' other negative evidence signal' : ' other negative evidence signals',
        );
      }
      parts.push(` for `, { text: skillNames, bold: true }, '.');
      result[`${key}_NEGATIVE_EVIDENCE`] = { type: 'negative', parts };
    }

    if ((highlight.recentHighCriticalityCount ?? 0) > 0) {
      result[`${key}_CRITICALITY`] = {
        type: 'negative',
        parts: [
          `Contributed to `,
          { text: `${highlight.recentHighCriticalityCount}`, bold: true },
          highlight.recentHighCriticalityCount === 1
            ? ` high-criticality evidence item in the last 30 days.`
            : ` high-criticality evidence items in the last 30 days.`,
        ],
      };
    } else if (highlight.recentHighCriticalityCount === 0) {
      result[`${key}_CRITICALITY`] = {
        type: 'positive',
        parts: [`No high-criticality work associated with this match in the last 30 days.`],
      };
    }

    if (highlight.latestEvidenceDate) {
      const latestDate = moment(highlight.latestEvidenceDate);
      const ageDays = Math.max(0, moment().startOf('day').diff(latestDate.startOf('day'), 'days'));
      result[`${key}_RECENCY`] =
        ageDays <= 365
          ? {
              type: 'positive',
              parts: [
                `Demonstrated `,
                { text: skillNames, bold: true },
                ` within the last year; most recently on `,
                { text: latestDate.format('DD MMM YYYY'), bold: true },
                '.',
              ],
            }
          : {
              type: 'negative',
              parts: [
                `No evidence of demonstrating `,
                { text: skillNames, bold: true },
                ` for `,
                { text: `${ageDays} days`, bold: true },
                `; last demonstrated on `,
                { text: latestDate.format('DD MMM YYYY'), bold: true },
                '.',
              ],
            };
    }
  }

  function getSubtypeCount(
    evidenceType: NonNullable<SkillHighlight['evidenceTypes']>[number] | undefined,
    subtype: string,
  ) {
    return (
      evidenceType?.subtypes.find(({ name }) => name.toUpperCase() === subtype)?.count || 0
    );
  }

  function getEvidenceCount(highlight: SkillHighlight) {
    const value = Number(highlight.evidenceCount ?? highlight.activitiesCount ?? 0);
    return Number.isFinite(value) ? value : 0;
  }

  function appendList(parts: StructuredHighlightsTextPart[], items: Exclude<StructuredHighlightsTextPart, string>[]) {
    items.forEach((item, index) => {
      if (index > 0) parts.push(index === items.length - 1 ? ' and ' : ', ');
      parts.push(item);
    });
  }

  function compactBreakdown(values: Array<{ name: string; count: number }> | undefined) {
    const populated = (values || []).filter(({ count }) => count > 0);
    if (populated.length <= 3) return populated;
    return [
      ...populated.slice(0, 3),
      {
        name: 'Other',
        count: populated.slice(3).reduce((total, item) => total + item.count, 0),
      },
    ];
  }

  function formatEvidenceLabel(value: string) {
    const normalized = value.trim().toUpperCase();
    if (normalized === 'GITLAB') return 'GitLab';
    if (normalized === 'SKILL_FEEDBACK') return 'skill feedback';
    return value
      .trim()
      .toLowerCase()
      .replace(/(^|[_\s-])\w/g, (match) => match.toUpperCase())
      .replace(/_/g, ' ');
  }

  function performanceRankLabel(performanceRank: number) {
    const rankLabels: Record<number, string> = {
      1: 'Top 10%',
      2: 'Top 30%',
      3: 'Middle 40%',
      4: 'Bottom 30%',
      5: 'Bottom 10%',
    };

    const parts: StructuredHighlightsTextPart[] = [`Performance rating: `];

    parts.push({ text: rankLabels[performanceRank], bold: true });

    parts.push('.');

    return parts;
  }

  function missingSkillsLabel() {
    const allSkills = new Set<string>();
    for (const { name } of skillGroups.missingSkills ?? []) {
      allSkills.add(name);
    }

    const parts: StructuredHighlightsTextPart[] = [
      { text: `No experience`, bold: true },
      ` found related to `,
    ];

    const allSkillsR = Array.from(allSkills);
    for (let i = 0; i < allSkillsR.length; i++) {
      if (i === 0) parts.push({ text: allSkillsR[i], bold: true });
      if (i > 0 && i < allSkillsR.length - 1)
        parts.push(`, `, { text: allSkillsR[i], bold: true });
      if (i > 0 && i === allSkillsR.length - 1)
        parts.push(` and `, { text: allSkillsR[i], bold: true });
    }

    parts.push(`.`);

    return parts;
  }
}
